<script setup>
import { ref, computed, watch } from 'vue'
import { useEmailTemplatesStore } from '@/stores'
import GrapesEditor from './GrapesEditor.vue'
import EmailTemplateFieldInserter from './EmailTemplateFieldInserter.vue'
import EmailTemplatePreview from './EmailTemplatePreview.vue'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
  selectedTemplate: {
    type: Object,
    default: null,
  },
  allowTemplateNameEdit: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:isDialogVisible', 'saved'])

const emailTemplatesStore = useEmailTemplatesStore()

// Form state
const saving = ref(false)
const sendingTestEmail = ref(false)
const activeTab = ref('editor')
const showPreview = ref(false)
const testEmailAddress = ref('')
const showTestEmailDialog = ref(false)
const showHtmlSourceDialog = ref(false)
const htmlSourceContent = ref('')
const changeNotes = ref('')

// Form fields
const formData = ref({
  templateName: '',
  displayName: '',
  description: '',
  content: '',
  defaultSubject: '',
  defaultPreheader: '',
  dataFieldGroup: 'system',
  defaultFrom: 'preferred',
  defaultCc: [],
  defaultBcc: [],
})

// Allowed sender options (loaded from server) — drives the From/CC/BCC pickers.
const senderOptions = computed(() =>
  emailTemplatesStore.allowedSenders.map(s => ({
    value: s.key,
    title: `${s.displayName} <${s.email}>`,
  })),
)

// Validation rules
const rules = {
  required: v => !!v || 'Required',
  templateName: v => /^[a-zA-Z0-9_-]+$/.test(v) || 'Only letters, numbers, underscores, and hyphens allowed',
}

// Editor ref
const editorRef = ref(null)

// Available field groups
const fieldGroupOptions = [
  { title: 'User', value: 'user' },
  { title: 'Reservation', value: 'reservation' },
  { title: 'User Group', value: 'userGroup' },
  { title: 'Restaurant', value: 'restaurant' },
  { title: 'System', value: 'system' },
  { title: 'Verification', value: 'verification' },
  { title: 'Membership', value: 'membership' },
  { title: 'Partner', value: 'partner' },
]

// Get field group for current template
const currentFieldGroup = computed(() => {
  if (!emailTemplatesStore.fieldGroups) return null

  return emailTemplatesStore.fieldGroups[formData.value.dataFieldGroup]
})

// Display title for the locked Data Field Group (matches dropdown labels)
const dataFieldGroupTitle = computed(() => {
  const match = fieldGroupOptions.find(opt => opt.value === formData.value.dataFieldGroup)

  return match ? match.title : formData.value.dataFieldGroup
})

// Dialog visibility
const dialogVisible = computed({
  get: () => props.isDialogVisible,
  set: val => emit('update:isDialogVisible', val),
})

// Load field groups on mount
const loadFieldGroups = async () => {
  if (!emailTemplatesStore.fieldGroups) {
    await emailTemplatesStore.getFieldGroups()
  }
}

// Load allowed senders (from / cc / bcc allowlist) on mount.
const loadAllowedSenders = async () => {
  if (!emailTemplatesStore.allowedSenders?.length) {
    await emailTemplatesStore.getAllowedSenders()
  }
}

// Initialize form when template changes
watch(() => props.selectedTemplate, async template => {
  if (template) {
    formData.value = {
      templateName: template.templateName || '',
      displayName: template.displayName || '',
      description: template.description || '',
      content: template.content || '',
      defaultSubject: template.defaultSubject || '',
      defaultPreheader: template.defaultPreheader || '',
      dataFieldGroup: template.dataFieldGroup || 'system',
      defaultFrom: template.defaultFrom || 'preferred',
      defaultCc: Array.isArray(template.defaultCc) ? [...template.defaultCc] : [],
      defaultBcc: Array.isArray(template.defaultBcc) ? [...template.defaultBcc] : [],
    }
    changeNotes.value = ''
    await Promise.all([loadFieldGroups(), loadAllowedSenders()])
  }
}, { immediate: true })

// RM-227: a rejected save (malformed Liquid, encoded link) returns a 400 with
// a plain-English reason — show it in the dialog instead of only the console.
const saveError = ref('')

// Save template
const saveTemplate = async () => {
  if (!props.selectedTemplate?._id) return

  saving.value = true
  saveError.value = ''
  try {
    const updateData = {
      ...formData.value,
      changeNotes: changeNotes.value,
    }

    // dataFieldGroup is set on creation only — never include in updates
    delete updateData.dataFieldGroup

    // Only include templateName if editing is allowed (for cloned templates)
    if (!props.allowTemplateNameEdit) {
      delete updateData.templateName
    }
    await emailTemplatesStore.updateTemplate(props.selectedTemplate._id, updateData)
    emit('saved')
    dialogVisible.value = false
  } catch (error) {
    console.error('Error saving template:', error)
    saveError.value = error.response?.data?.error
      || 'The template could not be saved. Check your connection and try again.'
  } finally {
    saving.value = false
  }
}

// Send test email
const openTestEmailDialog = () => {
  showTestEmailDialog.value = true
}

const sendTestEmail = async () => {
  if (!testEmailAddress.value || !props.selectedTemplate?._id) return

  sendingTestEmail.value = true
  try {
    await emailTemplatesStore.sendTestEmail(
      props.selectedTemplate._id,
      testEmailAddress.value,
      formData.value.defaultSubject,
      formData.value.defaultPreheader,
    )
    showTestEmailDialog.value = false
    testEmailAddress.value = ''
  } catch (error) {
    console.error('Error sending test email:', error)
  } finally {
    sendingTestEmail.value = false
  }
}

// View/edit HTML source
const openHtmlSource = () => {
  htmlSourceContent.value = formData.value.content
  showHtmlSourceDialog.value = true
}

const applyHtmlSource = () => {
  formData.value.content = htmlSourceContent.value
  showHtmlSourceDialog.value = false
}

// Close dialog
const closeDialog = () => {
  saveError.value = ''
  dialogVisible.value = false
}

// Copy template name to clipboard
const copyTemplateName = async () => {
  if (props.selectedTemplate?.templateName) {
    try {
      await navigator.clipboard.writeText(props.selectedTemplate.templateName)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
}

// Handle editor ready
const onEditorReady = () => {}

// RM-227: a field clicked in the Fields tab lands where the cursor was in
// whichever content tab was used last (visual editor or HTML source), then
// that tab is shown so the insertion is visible.
const lastContentTab = ref('editor')
const sourceCaret = ref(null)
watch(activeTab, tab => {
  if (tab === 'editor' || tab === 'source') lastContentTab.value = tab
})

const rememberSourceCaret = event => {
  const el = event?.target
  if (el && typeof el.selectionStart === 'number') sourceCaret.value = el.selectionStart
}

const insertField = field => {
  const syntax = `{{ ${field.path} }}`
  if (lastContentTab.value === 'source') {
    const content = formData.value.content || ''
    const at = sourceCaret.value ?? content.length
    formData.value.content = `${content.slice(0, at)}${syntax}${content.slice(at)}`
    sourceCaret.value = at + syntax.length
  } else {
    editorRef.value?.insertHtml(syntax)
  }
  activeTab.value = lastContentTab.value
}
</script>

<template>
  <VDialog
    v-model="dialogVisible"
    max-width="1800"
    :persistent="saving"
    scrollable
  >
    <VCard
      v-if="selectedTemplate"
      class="email-template-dialog"
    >
      <!-- Header -->
      <VCardTitle class="d-flex align-center pa-4">
        <VIcon class="mr-2">
          mdi-email-edit
        </VIcon>
        <span>Edit Email Template</span>
        <VChip
          class="ml-3"
          size="small"
          color="primary"
        >
          v{{ selectedTemplate.version }}
        </VChip>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          @click="closeDialog"
        />
      </VCardTitle>

      <VDivider />

      <!-- Content -->
      <VCardText class="pa-0">
        <VRow
          no-gutters
          class="fill-height"
        >
          <!-- Left Column: Editor -->
          <VCol
            cols="12"
            :lg="showPreview ? 8 : 12"
          >
            <div class="pa-4">
              <!-- Template Name (Code Reference) -->
              <VRow class="mb-2">
                <VCol cols="12">
                  <!-- Editable template name for cloned templates -->
                  <VTextField
                    v-if="allowTemplateNameEdit"
                    v-model="formData.templateName"
                    label="Template Name (Code Reference) *"
                    variant="outlined"
                    density="compact"
                    hint="Unique identifier (e.g., 'welcome-email', 'password_reset'). Edit this before saving!"
                    :rules="[rules.required, rules.templateName]"
                    persistent-hint
                  >
                    <template #append-inner>
                      <VChip
                        size="x-small"
                        color="warning"
                        class="mr-2"
                      >
                        New
                      </VChip>
                    </template>
                  </VTextField>
                  <!-- Read-only template name for existing templates -->
                  <VTextField
                    v-else
                    :model-value="selectedTemplate?.templateName"
                    label="Template Name (Code Reference)"
                    variant="outlined"
                    density="compact"
                    readonly
                    bg-color="grey-lighten-4"
                    hint="Use this name in code: sendEmail('templateName')"
                    persistent-hint
                  >
                    <template #append-inner>
                      <VBtn
                        icon="mdi-content-copy"
                        size="small"
                        variant="text"
                        title="Copy template name"
                        @click="copyTemplateName"
                      />
                    </template>
                  </VTextField>
                </VCol>
              </VRow>

              <!-- Template Info -->
              <VRow class="mb-4">
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="formData.displayName"
                    label="Display Name"
                    variant="outlined"
                    density="compact"
                    hint="Human-readable name for this template"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    :model-value="dataFieldGroupTitle"
                    label="Data Field Group"
                    variant="outlined"
                    density="compact"
                    readonly
                    bg-color="grey-lighten-4"
                    hint="Set on creation only — cannot be changed"
                    persistent-hint
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="formData.description"
                    label="Description"
                    variant="outlined"
                    density="compact"
                  />
                </VCol>
              </VRow>

              <!-- Sender Settings -->
              <VRow class="mb-4">
                <VCol
                  cols="12"
                  md="4"
                >
                  <VSelect
                    v-model="formData.defaultFrom"
                    label="From"
                    variant="outlined"
                    density="compact"
                    :items="senderOptions"
                    item-title="title"
                    item-value="value"
                    hint="Falls back to system default if unset"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VSelect
                    v-model="formData.defaultCc"
                    label="CC"
                    variant="outlined"
                    density="compact"
                    multiple
                    chips
                    closable-chips
                    clearable
                    :items="senderOptions"
                    item-title="title"
                    item-value="value"
                    hint="Adds to any dynamic CCs for the event"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VSelect
                    v-model="formData.defaultBcc"
                    label="BCC"
                    variant="outlined"
                    density="compact"
                    multiple
                    chips
                    closable-chips
                    clearable
                    :items="senderOptions"
                    item-title="title"
                    item-value="value"
                    hint="Overrides the default BCC when set"
                  />
                </VCol>
              </VRow>

              <VRow class="mb-4">
                <VCol cols="12">
                  <VTextarea
                    v-model="formData.defaultSubject"
                    label="Default Subject Line"
                    variant="outlined"
                    density="compact"
                    rows="2"
                    auto-grow
                    hint="Can use LiquidJS syntax: {{ options.user.firstName }}"
                  />
                </VCol>
              </VRow>

              <!-- Toolbar -->
              <div class="d-flex align-center mb-3">
                <VBtnToggle
                  v-model="activeTab"
                  mandatory
                  density="compact"
                  class="mr-3"
                >
                  <VBtn
                    value="editor"
                    size="small"
                  >
                    <VIcon size="small">
                      mdi-pencil
                    </VIcon>
                    <span class="ml-1">Visual Editor</span>
                  </VBtn>
                  <VBtn
                    value="source"
                    size="small"
                  >
                    <VIcon size="small">
                      mdi-code-braces
                    </VIcon>
                    <span class="ml-1">HTML Source</span>
                  </VBtn>
                  <VBtn
                    value="fields"
                    size="small"
                  >
                    <VIcon size="small">
                      mdi-code-tags
                    </VIcon>
                    <span class="ml-1">Fields</span>
                  </VBtn>
                </VBtnToggle>

                <VSpacer />

                <VBtn
                  size="small"
                  :variant="showPreview ? 'flat' : 'outlined'"
                  :color="showPreview ? 'primary' : undefined"
                  @click="showPreview = !showPreview"
                >
                  <VIcon size="small">
                    mdi-eye
                  </VIcon>
                  <span class="ml-1">Preview</span>
                </VBtn>
              </div>

              <!-- Editor / Source / Fields Tabs -->
              <VWindow v-model="activeTab">
                <!-- Visual Editor (GrapeJS) -->
                <VWindowItem value="editor">
                  <GrapesEditor
                    ref="editorRef"
                    v-model="formData.content"
                    height="500px"
                    @ready="onEditorReady"
                  />
                </VWindowItem>

                <!-- HTML Source -->
                <VWindowItem value="source">
                  <div class="source-editor-container">
                    <VTextarea
                      v-model="formData.content"
                      variant="outlined"
                      rows="20"
                      class="html-source-textarea"
                      hide-details
                      @click="rememberSourceCaret"
                      @keyup="rememberSourceCaret"
                      @blur="rememberSourceCaret"
                    />
                    <p class="text-caption text-grey mt-2">
                      Edit the raw HTML source. Use LiquidJS syntax:
                      <code v-pre>{{ options.fieldName }}</code> for output,
                      <code v-pre>{% if options.user.verified %}</code> for logic.
                    </p>
                  </div>
                </VWindowItem>

                <!-- Field Inserter -->
                <VWindowItem value="fields">
                  <EmailTemplateFieldInserter
                    :field-group="currentFieldGroup"
                    :group-name="formData.dataFieldGroup"
                    @insert="insertField"
                  />
                </VWindowItem>
              </VWindow>

              <!-- Change Notes -->
              <VTextarea
                v-model="changeNotes"
                label="Change Notes (optional)"
                variant="outlined"
                density="compact"
                rows="2"
                class="mt-4"
                hint="Describe what you changed for version history"
              />
            </div>
          </VCol>

          <!-- Right Column: Preview -->
          <VCol
            v-if="showPreview"
            cols="12"
            lg="4"
            class="preview-column"
          >
            <div class="pa-4 h-100">
              <EmailTemplatePreview
                :content="formData.content"
                :data-field-group="formData.dataFieldGroup"
                :field-group="currentFieldGroup"
                :preheader="formData.defaultPreheader"
                :template-id="selectedTemplate?._id"
              />
            </div>
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <!-- Save error (RM-227: the server refuses malformed Liquid with a reason) -->
      <VAlert
        v-if="saveError"
        type="error"
        variant="tonal"
        density="compact"
        closable
        class="ma-4 mb-0"
        @click:close="saveError = ''"
      >
        {{ saveError }}
      </VAlert>

      <!-- Actions -->
      <VCardActions class="pa-4">
        <VBtn
          variant="outlined"
          :disabled="saving"
          @click="openTestEmailDialog"
        >
          <VIcon start>
            mdi-email-send
          </VIcon>
          Send Test Email
        </VBtn>

        <VSpacer />

        <VBtn
          variant="text"
          :disabled="saving"
          @click="closeDialog"
        >
          Cancel
        </VBtn>

        <VBtn
          color="primary"
          :loading="saving"
          @click="saveTemplate"
        >
          <VIcon start>
            mdi-content-save
          </VIcon>
          Save Template
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Test Email Dialog -->
  <VDialog
    v-model="showTestEmailDialog"
    max-width="400"
  >
    <VCard>
      <VCardTitle>Send Test Email</VCardTitle>
      <VCardText>
        <VTextField
          v-model="testEmailAddress"
          label="Email Address"
          type="email"
          variant="outlined"
          density="compact"
          placeholder="Enter email address"
          autofocus
        />
        <p class="text-caption text-grey">
          A test email will be sent with sample data to preview how the template looks.
        </p>
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          variant="text"
          @click="showTestEmailDialog = false"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          :loading="sendingTestEmail"
          :disabled="!testEmailAddress"
          @click="sendTestEmail"
        >
          Send
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.email-template-dialog {
  height: 95vh;
  display: flex;
  flex-direction: column;
}

.preview-column {
  border-left: 1px solid var(--ds-border-default);
  background-color: var(--ds-surface-subtle);
}

.source-editor-container {
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  padding: 16px;
  background-color: var(--ds-surface-subtle);
}

.html-source-textarea {
  :deep(textarea) {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
    font-size: 12px !important;
    line-height: 1.5;
  }
}
</style>
